<template>
  <div
    class="negocio-filter-container bg-[var(--fundoForm)] p-4 rounded-lg shadow-lg"
  >
    <!-- Filter Header -->
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-[var(--text)] font-semibold">Filtros da Tipologia</h3>
      <div class="flex items-center gap-3">
        <button
          @click="clearFilters"
          class="text-blue-400 hover:text-blue-300 text-sm underline"
        >
          Limpar Filtros
        </button>
        <button
          @click="closeFilter"
          class="text-gray-400 hover:text-[var(--text)] text-lg transition-colors"
          title="Fechar filtros"
        >
          <i class="fa-solid fa-times"></i>
        </button>
      </div>
    </div>

    <!-- Dynamic Filter Fields from Metadata -->
    <div
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mt-4"
    >
      <!-- Text Search Filters -->
      <div class="filter-group">
        <label class="filter-label">Nome</label>
        <input
          v-model="filters.nome_search"
          type="text"
          placeholder="Ex: Nome do equipamento..."
          @input="onFilterChange"
          class="filter-input"
        />
      </div>

      <div class="filter-group">
        <label class="filter-label">Codigo</label>
        <input
          v-model="filters.codigo_search"
          type="text"
          placeholder="Ex: Codigo do equipamento..."
          @input="onFilterChange"
          class="filter-input"
        />
      </div>

      <div class="filter-group">
        <label class="filter-label">Associação</label>
        <select
          v-model="filters.com_associacao"
          @change="onFilterChange"
          class="filter-input"
        >
          <option value="">Todos</option>
          <option value="pais">Principal</option>
          <option value="filhos">Associado</option>
        </select>
      </div>

      <!-- Render filters dynamically from metadata -->

      <div
        v-for="filterConfig in computedFilters"
        :key="filterConfig.key"
        class="filter-group"
      >
        <label class="filter-label">{{ filterConfig.label }}</label>

        <!-- Text Input -->
        <input
          v-if="filterConfig.type === 'text'"
          v-model="filters[filterConfig.key]"
          type="text"
          :placeholder="`Digite ${filterConfig.label.toLowerCase()}...`"
          @input="onFilterChange"
          class="filter-input"
        />

        <!-- Select Input -->
        <select
          v-else-if="filterConfig.type === 'select'"
          v-model="filters[filterConfig.key]"
          @change="onFilterChange"
          class="filter-input"
        >
          <option value="">
            {{ `Selecione ${filterConfig.label.toLowerCase()}...` }}
          </option>
          <option
            v-for="option in filterConfig.options"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>

        <!-- Multiselect Input -->
        <Multiselect
          v-else-if="filterConfig.type === 'multiselect'"
          v-model="filters[filterConfig.key]"
          :options="filterConfig.options"
          :multiple="true"
          :searchable="true"
          :close-on-select="false"
          track-by="value"
          label="label"
          :placeholder="`Selecione ${filterConfig.label.toLowerCase()}...`"
          @input="onFilterChange"
          class="multiselect-dark"
        />
      </div>
    </div>
    <!-- Apply Filters Button -->
    <div class="flex justify-end mt-4">
      <button
        @click="applyFilters"
        class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      >
        Aplicar Filtros
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import axios from "axios";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.css";

// Props
const props = defineProps({
  allowedStatuses: {
    type: Array,
    default: null, // null means all statuses allowed
  },
  defaultStatus: {
    type: String,
    default: null, // default status when "all" is selected
  },
  restrictToStatuses: {
    type: Array,
    default: null, // Backend restriction - only return these statuses
  },
});

// Emits
const emit = defineEmits(["filtersChanged", "close-filter"]);

// Filter state
const filters = ref({
  status: "",
  nome_search: "",
  codigo_search: "",
  familia_id: "",
  produto_id: "",
  tipologia_id: "",
  tpMarca_id: "",
  tpMedida_id: "",
  tpServico_id: "",
  com_associacao: "",
});

// Filter metadata from backend
const filterMetadata = ref([]);

// Filter options from metadata
const statusOptions = ref([]);

// Handle filter changes
const onFilterChange = () => {
  applyFilters();
};

// Load filter metadata
const loadMetadata = async () => {
  try {
    const response = await axios.get(
      `${process.env.VUE_APP_URL_BASE}/api/v1/equipamento/metadata/`,
    );
    const metadata = response.data.data;
    // console.log('Tipologia Metadata:', metadata)

    // Extract all filters from metadata
    if (metadata.filters && Array.isArray(metadata.filters)) {
      // Filter out restricted keys and apply status filtering
      filterMetadata.value = metadata.filters.map((filter) => {
        // Apply allowedStatuses filter if this is the status filter
        if (
          filter.key === "status" &&
          props.allowedStatuses &&
          props.allowedStatuses.length > 0
        ) {
          return {
            ...filter,
            options: filter.options.filter((opt) =>
              props.allowedStatuses.includes(opt.value),
            ),
          };
        }
        return filter;
      });

      // Also set statusOptions for backward compatibility
      const statusFilter = metadata.filters.find((f) => f.key === "status");
      if (statusFilter) {
        let statusOpts = statusFilter.options || [];
        if (props.allowedStatuses && props.allowedStatuses.length > 0) {
          statusOpts = statusOpts.filter((opt) =>
            props.allowedStatuses.includes(opt.value),
          );
        }
        statusOptions.value = statusOpts;
      }
    }
  } catch (error) {
    console.error("Error loading tipologia metadata:", error);
  }
};

// Apply filters
const applyFilters = () => {
  // Create a copy of filters to process
  const filtersToSend = { ...filters.value };

  // Remove empty values
  const activeFilters = Object.fromEntries(
    Object.entries(filtersToSend).filter(
      ([, value]) => value !== "" && value !== null && value !== false,
    ),
  );
  // If no status filter is set and defaultStatus is provided, use the default
  if (!activeFilters.status && props.defaultStatus) {
    activeFilters.status = props.defaultStatus;
  }

  // Add backend restriction parameter if provided
  if (props.restrictToStatuses && props.restrictToStatuses.length > 0) {
    activeFilters.restrict_status = props.restrictToStatuses.join(",");
  }

  emit("filtersChanged", activeFilters);
};

// Clear filters
const clearFilters = () => {
  filters.value = {
    status: "",
    nome_search: "",
    codigo_search: "",
    familia_id: "",
    produto_id: "",
    tipologia_id: "",
    tpMarca_id: "",
    tpMedida_id: "",
    tpServico_id: "",
    com_associacao: "",
  };
  emit("filtersChanged", {});
};

// Close filter
const closeFilter = () => {
  emit("close-filter");
};

// Reset dependent fields when Familia changes
watch(() => filters.value.familia_id, () => {
  filters.value.produto_id = ''
  filters.value.tipologia_id = ''
  onFilterChange()
})

// Reset dependent field when Produto changes
watch(() => filters.value.produto_id, () => {
  filters.value.tipologia_id = ''
  onFilterChange()
})

const computedFilters = computed(() => {
  // 1. Get current selection values
  const selectedFamiliaId = filters.value.familia_id
  const selectedProdutoId = filters.value.produto_id

  // 2. Find the names associated with the selected IDs to use for label matching
  // We need to look inside the original metadata to find the labels
  const familiaFilter = filterMetadata.value.find(f => f.key === 'familia_id')
  const selectedFamiliaLabel = familiaFilter?.options.find(o => o.value == selectedFamiliaId)?.label

  const produtoFilter = filterMetadata.value.find(f => f.key === 'produto_id')
  const selectedProdutoLabel = produtoFilter?.options.find(o => o.value == selectedProdutoId)?.label

  return filterMetadata.value.map(filter => {
    let filteredOptions = [...(filter.options || [])]

    // --- LOGIC FOR PRODUTO ---
    if (filter.key === 'produto_id' && selectedFamiliaId && selectedFamiliaLabel) {
      filteredOptions = filteredOptions.filter(opt => {
        // Matches "Produto Name (Familia Name)"
        return opt.label.includes(`(${selectedFamiliaLabel})`)
      })
    }

    // --- LOGIC FOR TIPOLOGIA ---
    if (filter.key === 'tipologia_id') {
      // Filter by Familia if selected
      if (selectedFamiliaId && selectedFamiliaLabel) {
        filteredOptions = filteredOptions.filter(opt => opt.label.includes(selectedFamiliaLabel))
      }
      // Filter by Produto if selected
      if (selectedProdutoId && selectedProdutoLabel) {
        // We extract the pure product name from the label "Name (Family)"
        const pureProdutoName = selectedProdutoLabel.split(' (')[0]
        filteredOptions = filteredOptions.filter(opt => opt.label.includes(pureProdutoName))
      }
    }

    return { ...filter, options: filteredOptions }
  })
})

onMounted(() => {
  loadMetadata();
});
</script>

<style scoped>
.filter-group {
  @apply flex flex-col space-y-1;
}

.filter-label {
  @apply text-sm font-medium text-gray-300;
}

.filter-input {
  @apply bg-[var(--fundoForm)] border border-gray-600 rounded px-3 py-2 text-[var(--text)] text-sm;
  @apply focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500;
}

.filter-input:focus {
  @apply bg-opacity-80;
}

.negocio-filter-container {
  @apply border border-gray-700;
}
</style>
